/**
 * ConfirmActionDialog — Generic confirmation dialog that executes an async action.
 *
 * Used for simple confirm-before-execute actions like sending HL7 messages,
 * deleting images/reports, etc. The action function and display text are
 * provided via the studyActionStore's confirmMeta.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
} from '@mui/material';
import { useStudyActionStore } from '../../stores/studyActionStore';

interface ConfirmActionDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const ConfirmActionDialog = ({
  open,
  onClose,
  onComplete,
}: ConfirmActionDialogProps) => {
  const { confirmMeta } = useStudyActionStore();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const handleConfirm = async () => {
    if (!confirmMeta) return;
    setPending(true);
    setError(null);
    try {
      await confirmMeta.action();
      onComplete?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  if (!confirmMeta) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{confirmMeta.title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">{confirmMeta.message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={pending}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
